import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { PIN_OUTCOMES, PIN_MARKER_COLORS } from "@/lib/canvassingUtils";
import { Check, X } from "lucide-react";
import type { PinOutcome } from "@/hooks/useCanvassing";
import { cn } from "@/lib/utils";

interface QuickOutcomeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address?: string;
  currentOutcome?: PinOutcome;
  onPick: (outcome: PinOutcome) => void;
  onDelete?: () => void;
}

const OUTCOME_ICONS: Record<PinOutcome, string> = {
  not_knocked: "🚪",
  not_home: "🏚️",
  not_interested: "👎",
  callback: "📞",
  appointment_set: "📅",
  sold: "💰",
  already_customer: "⭐",
};

const OUTCOME_LABELS: Record<PinOutcome, string> = {
  not_knocked: "Not knocked yet",
  not_home: "Not home",
  not_interested: "Not interested",
  callback: "Callback",
  appointment_set: "Appointment set",
  sold: "Sold!",
  already_customer: "Already customer",
};

export function QuickOutcomeSheet({
  open, onOpenChange, address, currentOutcome, onPick, onDelete,
}: QuickOutcomeSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t pb-[env(safe-area-inset-bottom)] max-h-[70vh]"
      >
        <div className="space-y-3">
          <div className="text-center">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Mark this door</div>
            {address && (
              <div className="text-sm font-medium truncate mt-0.5">{address}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(OUTCOME_LABELS) as PinOutcome[])
              .filter(o => o !== "not_knocked")
              .map(outcome => {
                const isActive = currentOutcome === outcome;
                const color = PIN_MARKER_COLORS[outcome];
                return (
                  <button
                    key={outcome}
                    onClick={() => onPick(outcome)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-3 rounded-xl border-2 transition-all text-left",
                      "active:scale-95",
                      isActive ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-background hover:border-primary/40"
                    )}
                    style={isActive ? { borderColor: color } : undefined}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                      style={{ background: `${color}22` }}
                    >
                      {OUTCOME_ICONS[outcome]}
                    </div>
                    <div className="text-sm font-medium truncate">
                      {OUTCOME_LABELS[outcome]}
                    </div>
                    {isActive && <Check className="w-4 h-4 ml-auto text-primary" />}
                  </button>
                );
              })}
          </div>

          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Remove pin
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
